import React, { useLayoutEffect, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowLeft, FiArrowRight, FiCheck, FiClock, FiExternalLink, FiFileText, FiGlobe, FiHelpCircle, FiRefreshCw, FiTrendingUp } from "react-icons/fi";
import useConsultPopup from "./useConsultPopup";
import ConsultPopup from "./ConsultPopup";
import slideTo, { scrollToTop } from "./pageTransition";
import "./page-transition.css";
import "./subscription-cases.css";
import LocalizedContent, { useLocaleDocument } from "./localization";

const structure = [
  { icon: FiCheck, label: "기본 구독료", value: "월 200만원부터", desc: "설계·개발·운영에 들어가는 고정 비용" },
  { icon: FiTrendingUp, label: "성과 연동 요금", value: "모든 계약에 성과 축", desc: "회사가 커지면 요금도 함께 움직입니다" },
  { icon: FiRefreshCw, label: "5년 후 재계약", value: "그때의 규모로 재산정", desc: "성장한 규모에 맞춰 금액 재조정" },
];

const colorThemes = [
  { id: "default", label: "기본 블루", color: "#2878FF" },
  { id: "mint", label: "민트", color: "#0F5C5A" },
  { id: "ocean", label: "딥 블루", color: "#16324F" },
  { id: "indigo", label: "인디고", color: "#5865F2" },
];
const locales = [{ id:"ko", label:"한국어" },{ id:"en", label:"English" },{ id:"zh", label:"中文" },{ id:"ja", label:"日本語" },{ id:"vi", label:"Tiếng Việt" },{ id:"fil", label:"Filipino" }];

const contractCases = [
  {
    type: "구독자를 모으는 회사",
    industry: "상조 · 멤버십 · 정기결제",
    summary: "시스템이 구독자 모집과 유지에 직접 쓰이고, 구독자가 늘수록 요율이 낮아지는 구조",
    terms: [
      { label: "기본 구독료", value: "월 200만원", note: "60개월 고정" },
      { label: "성과 연동", value: "규모가 커질수록 인하", note: "구독자 수를 기준으로 산정" },
      { label: "정산 주기", value: "분기 정산", note: "시스템 데이터로 자동 산정" },
      { label: "5년 후", value: "구독자 규모 기준 재산정", note: "운영 중심 구독으로 전환 가능" },
    ],
    sample: "구독자가 늘어날수록 적용 요율이 내려가, 규모가 커져도 비용이 비례해 늘지 않습니다",
  },
  {
    type: "거래액이 오가는 회사",
    industry: "유통 · 이커머스 · 중개",
    summary: "시스템을 통해 주문과 정산이 처리되는 만큼 성과를 나눔",
    terms: [
      { label: "기본 구독료", value: "월 250만원", note: "정산·연동 범위가 넓어 기본료 상향" },
      { label: "성과 연동", value: "처리 거래액의 0.3%", note: "시스템을 거친 거래만 집계" },
      { label: "성과금 상한", value: "월 500만원", note: "급증해도 비용이 예측 가능" },
      { label: "5년 후", value: "거래 규모 기준 재산정", note: "상한선도 함께 재조정" },
    ],
    sample: "거래액 월 10억원 → 250만원 + 성과 300만원 = 월 550만원",
  },
  {
    type: "건 단위로 일하는 회사",
    industry: "의전 · 시공 · 물류 · 정비",
    summary: "매출 비율보다 처리 건수가 업무량을 정확히 반영하는 경우",
    terms: [
      { label: "기본 구독료", value: "월 200만원", note: "60개월 고정" },
      { label: "성과 연동", value: "처리 건당 1만원", note: "등록·완료된 건만 집계" },
      { label: "최소 보장", value: "기본료는 그대로 유지", note: "비수기에도 운영 동일" },
      { label: "5년 후", value: "연간 처리 건수 기준 재산정", note: "건당 단가 재협의" },
    ],
    sample: "월 200건 처리 → 200만원 + 성과 200만원 = 월 400만원",
  },
  {
    type: "내부 업무가 중심인 회사",
    industry: "제조 · 전문 서비스 · 공공",
    summary: "매출 연동이 어려운 대신 사용자 수를 성과 축으로 삼는 구조",
    terms: [
      { label: "기본 구독료", value: "월 300만원", note: "사용자 30명까지 포함" },
      { label: "성과 연동", value: "초과 사용자 1인당 월 2만원", note: "실제 계정 수로 자동 집계" },
      { label: "범위 추가", value: "신규 업무 영역당 월 50만원", note: "합의 후 단계적으로 확장" },
      { label: "5년 후", value: "사용자 수·업무 범위 기준 재산정", note: "1인당 단가 재협의" },
    ],
    sample: "사용자 130명 → 300만원 + 초과 100명 × 2만원 = 월 500만원",
  },
];

const operationStructure = [
  { icon: FiCheck, label: "기본 운영료", value: "월 350만원부터" },
  { icon: FiTrendingUp, label: "개선 개발", value: "합의된 범위 내 포함" },
  { icon: FiRefreshCw, label: "범위 재조정", value: "시스템 규모에 따라 협의" },
];

const operationCases = [
  { type: "단일 업무 시스템", industry: "사내 관리자 · 백오피스", summary: "한 개의 핵심 업무 시스템을 안정적으로 운영하고 필요한 개선을 이어가는 구조", terms: [{ label: "기본 운영료", value: "월 350만원", note: "단일 시스템 기준" }, { label: "개선 개발", value: "월간 개선 요청 대응", note: "우선순위 협의 후 진행" }, { label: "장애 대응", value: "업무시간 4시간 이내", note: "심각도에 따라 우선 대응" }, { label: "정기 보고", value: "월 1회", note: "운영 현황과 개선 내역 공유" }], sample: "관리자 시스템 1개 + 서버·백업 운영 + 월간 개선 개발" },
  { type: "매출 핵심 시스템", industry: "예약 · 주문 · 결제", summary: "서비스 중단이 매출과 바로 연결되는 시스템을 우선순위 높게 관리하는 구조", terms: [{ label: "기본 운영료", value: "월 500만원", note: "고객용 서비스 포함" }, { label: "개선 개발", value: "월간 개선 + 전환율 개선", note: "운영 데이터 기반 개선" }, { label: "긴급 대응", value: "중대 장애 1시간 내", note: "24시간 알림 체계" }, { label: "정기 보고", value: "월 2회", note: "장애·성능·개선 현황" }], sample: "고객 서비스 + 관리자 시스템 + 결제 연동 + 긴급 장애 대응" },
  { type: "여러 시스템 통합 운영", industry: "ERP · CRM · 외부 연동", summary: "여러 시스템과 외부 서비스 사이의 데이터 흐름까지 함께 관리하는 구조", terms: [{ label: "기본 운영료", value: "월 700만원", note: "3개 시스템 기준" }, { label: "개선 개발", value: "월간 개선 + 연동 작업", note: "외부 API·자동화 포함" }, { label: "장애 대응", value: "중대 장애 2시간 내", note: "영향 범위 우선 확인" }, { label: "운영 회의", value: "격주 1회", note: "부서별 요청 통합 관리" }], sample: "ERP·CRM·백오피스 3개 + 외부 API 연동과 데이터 배치 운영" },
  { type: "전담팀형 운영", industry: "상시 개선 · 빠른 사업 변화", summary: "운영과 개발 요청이 많은 회사를 위해 전담 인력을 중심으로 협업하는 구조", terms: [{ label: "기본 운영료", value: "월 1,000만원", note: "전담 운영 체계" }, { label: "개선 개발", value: "상시 개선 + 로드맵 관리", note: "기획·개발·검수 포함" }, { label: "긴급 대응", value: "중대 장애 1시간 내", note: "담당자 직접 연락" }, { label: "운영 회의", value: "주 1회", note: "로드맵과 요청 우선순위 관리" }], sample: "전담 PM·개발자 배정 + 주간 회의 + 상시 운영·개선 개발" },
];

const grantStructure = [
  { icon: FiCheck, label: "구축비", value: "3,000만원부터" },
  { icon: FiFileText, label: "집행 비목", value: "외주용역비" },
  { icon: FiClock, label: "협약 종료 후", value: "운영 구독으로 전환" },
];

const grantCases = [
  {
    type: "초기창업패키지",
    industry: "창업 3년 이내",
    summary: "사업화자금의 외주용역비 비목으로 시스템 구축비를 집행하는 구조",
    terms: [
      { label: "구축비", value: "3,000만원부터", note: "화면 10개 기준, 부가세 별도" },
      { label: "협약기간", value: "약 8~10개월", note: "연도·기관별 공고에 따라 다름" },
      { label: "증빙 서류", value: "견적서·계약서·검수확인서", note: "주관기관 제출용 일체 제공" },
      { label: "전용 개발분", value: "고객사 귀속", note: "지원사업 규정에 따름" },
    ],
    sample: "구축비 3,000만원을 외주용역비로 집행 → 화면 10개, 협약기간 안에 검수 완료",
  },
  {
    type: "예비창업패키지",
    industry: "창업 전 예비창업자",
    summary: "법인 설립 전후에 최소 기능부터 만들어 협약기간 안에 검증하는 구조",
    terms: [
      { label: "구축비", value: "3,000만원부터", note: "핵심 화면 우선, 부가세 별도" },
      { label: "협약기간", value: "약 8~10개월", note: "연도·기관별 공고에 따라 다름" },
      { label: "개발 순서", value: "검증에 필요한 화면부터", note: "가설 확인용 기능 우선" },
      { label: "전용 개발분", value: "고객사 귀속", note: "지원사업 규정에 따름" },
    ],
    sample: "고객 신청 화면과 관리자 백오피스부터 열어 협약기간 내 실사용 검증",
  },
  {
    type: "창업도약패키지",
    industry: "창업 3~7년 · 매출 발생",
    summary: "이미 돌아가는 업무를 시스템으로 정비하고 범위를 넓히는 구조",
    terms: [
      { label: "구축비", value: "범위에 따라 산정", note: "화면 수·연동 건수 기준" },
      { label: "협약기간", value: "약 8~10개월", note: "연도·기관별 공고에 따라 다름" },
      { label: "기존 자료 이관", value: "엑셀·기존 프로그램", note: "구축비에 포함" },
      { label: "협약 종료 후", value: "전담 운영 구독 전환", note: "선택 사항" },
    ],
    sample: "엑셀로 하던 주문·정산을 시스템으로 옮기고, 종료 후 운영 구독으로 전환",
  },
  {
    type: "지자체 · 기타 지원사업",
    industry: "지자체 · 민간 매칭 · TIPS 등",
    summary: "사업마다 집행 규정이 달라 계약 형태를 먼저 맞추는 구조",
    terms: [
      { label: "구축비", value: "지원 규모에 맞춰 산정", note: "집행 가능액 확인 후 범위 확정" },
      { label: "협약기간", value: "사업별 상이", note: "공고 확인 후 일정 역산" },
      { label: "집행 비목", value: "사전 확인 필요", note: "주관기관 세부관리기준에 따름" },
      { label: "증빙 양식", value: "주관기관 양식에 맞춤", note: "요구 서식으로 작성" },
    ],
    sample: "공고와 세부관리기준을 먼저 확인해 집행 가능한 계약 형태를 정한 뒤 착수",
  },
];

const serpStructure = [
  { icon: FiClock, label: "상태", value: "출시 준비 중" },
  { icon: FiCheck, label: "대상", value: "상조 기업" },
  { icon: FiRefreshCw, label: "데모", value: "로그인 화면에서 체험" },
];

const serpModules = [
  {
    type: "통합 대시보드",
    industry: "경영 · 현장 공통",
    summary: "오늘 처리할 업무와 확인이 필요한 건을 한 화면에서 봅니다",
    terms: [
      { label: "핵심 지표", value: "의전·상담·발주 현황", note: "접속 즉시 집계" },
      { label: "업무 흐름", value: "진행 상태별 목록", note: "정상 진행·확인 필요 구분" },
      { label: "확인 주기", value: "별도 조회 없이", note: "화면을 열면 최신 상태" },
      { label: "연결 범위", value: "상담부터 정산까지", note: "모든 메뉴의 데이터" },
    ],
    sample: "진행 중인 의전, 오늘 신규 상담, 발주 대기 건을 접속하자마자 확인",
  },
  {
    type: "상담·고객관리",
    industry: "영업 · 상담 담당",
    summary: "상담 접수부터 담당자 배정, 고객 정보까지 한 곳에서 처리합니다",
    terms: [
      { label: "상담 접수", value: "채널별 기록", note: "접수 시점이 함께 남음" },
      { label: "담당자 배정", value: "접수 건별 지정", note: "배정 이력 유지" },
      { label: "고객 정보", value: "계약 이력과 연결", note: "중복 입력 없음" },
      { label: "후속 관리", value: "진행 상태 추적", note: "놓치는 건 방지" },
    ],
    sample: "상담 접수 → 담당자 배정 → 계약 전환까지 하나의 기록으로 이어짐",
  },
  {
    type: "의전·주문관리",
    industry: "현장 운영",
    summary: "의전 일정과 필요한 물품을 함께 관리해 현장에서 누락이 없게 합니다",
    terms: [
      { label: "일정 관리", value: "의전 건별 일정", note: "담당자와 함께 배정" },
      { label: "물품 확인", value: "필요 품목 점검", note: "재고와 연결" },
      { label: "진행 상태", value: "단계별 기록", note: "현장에서 갱신" },
      { label: "이력 조회", value: "고객 정보와 연결", note: "상담 기록까지 추적" },
    ],
    sample: "의전 일정과 물품을 같은 화면에서 확인해 현장 준비 누락을 막음",
  },
  {
    type: "발주·재고관리",
    industry: "구매 · 협력업체",
    summary: "행사에 쓰인 용품이 재고와 정산에 바로 반영됩니다",
    terms: [
      { label: "발주 관리", value: "협력업체별 발주", note: "현황을 한눈에" },
      { label: "재고 연동", value: "출고 즉시 차감", note: "의전 물품과 자동 연결" },
      { label: "발주 상태", value: "대기·완료 구분", note: "지연 건 표시" },
      { label: "정산 반영", value: "재고·발주 내역 그대로", note: "재입력 없이 자동 집계" },
    ],
    sample: "용품 출고 → 재고 차감 → 협력업체 발주 → 정산까지 자동 연결",
  },
  {
    type: "정산관리",
    industry: "회계 · 정산",
    summary: "행사별 매출·수수료·지급액을 자동 집계합니다",
    terms: [
      { label: "정산 대상", value: "행사·발주 내역 기준", note: "시스템 기록으로 산정" },
      { label: "집계 방식", value: "기간별 자동 집계", note: "수기 정리 불필요" },
      { label: "확인 절차", value: "담당자 검토 후 확정", note: "변경 이력 보존" },
      { label: "외부 연동", value: "회계 자료 연계", note: "사전 진단 후 범위 확정" },
    ],
    sample: "행사와 발주 기록이 그대로 정산 자료가 되어 다시 입력할 일이 없음",
  },
  {
    type: "협력 의전본부 관리",
    industry: "본사 · 협력 본부",
    summary: "본사·협력 본부·장례지도사별로 볼 수 있는 범위를 분리합니다",
    terms: [
      { label: "권한 분리", value: "조직별 접근 범위", note: "보여줄 정보와 숨길 정보 구분" },
      { label: "배정 관리", value: "본부별 장례지도사", note: "소속에 따라 업무 배정" },
      { label: "실적 조회", value: "본부별 현황", note: "권한 범위 내 집계" },
      { label: "정산 분리", value: "본부별 정산", note: "각자의 정산 내역만 확인" },
    ],
    sample: "협력 의전본부마다 자기 소속 장례지도사와 정산만 보이도록 분리",
  },
  {
    type: "현장 모바일 앱",
    industry: "장례지도사",
    summary: "장례지도사가 현장에서 바로 확인하고 입력합니다",
    terms: [
      { label: "배정 확인", value: "오늘 업무 목록", note: "앱 접속 즉시 확인" },
      { label: "현장 입력", value: "진행 상태 갱신", note: "본사에 실시간 반영" },
      { label: "물품 확인", value: "필요 용품 점검", note: "현장에서 바로 조회" },
      { label: "설치 없이", value: "휴대폰 브라우저", note: "별도 앱 설치 불필요" },
    ],
    sample: "장례지도사가 현장에서 배정 확인, 진행 입력, 물품 점검을 바로 처리",
  },
  {
    type: "카카오 알림톡",
    industry: "고객·담당자 안내",
    summary: "고객과 담당자에게 안내 메시지를 자동 발송합니다",
    terms: [
      { label: "발송 시점", value: "업무 단계별 자동", note: "접수·배정·완료 등" },
      { label: "수신 대상", value: "고객·담당자", note: "상황에 맞게 선택" },
      { label: "내용 구성", value: "템플릿 기반", note: "업무 데이터 자동 삽입" },
      { label: "발송 이력", value: "건별 기록", note: "발송 여부 확인 가능" },
    ],
    sample: "행사 접수 시 고객 안내, 배정 시 장례지도사 안내를 자동 발송",
  },
];

// /serp/features 전용 추가 콘텐츠 (한국어만)
const serpPainPoints = [
  "행사가 몰리면 접수·배정 현황을 전화와 카톡으로 일일이 확인한다",
  "월말 정산 때마다 엑셀을 맞추느라 며칠씩 걸린다",
  "용품 재고가 어디에 얼마나 남았는지 바로 알 수 없다",
  "협력 의전본부마다 보여줘야 할 정보와 숨겨야 할 정보가 다르다",
];

const serpWorkflow = [
  { step: "1", title: "행사 접수", who: "본사" },
  { step: "2", title: "장례지도사 배정", who: "협력 의전본부" },
  { step: "3", title: "현장 진행", who: "장례지도사, 모바일 앱" },
  { step: "4", title: "용품 출고·재고 차감", who: "자동" },
  { step: "5", title: "정산 집계", who: "자동" },
  { step: "6", title: "월 마감·실적 확인", who: "본사" },
];

const serpBenefits = [
  { title: "초기 구축비 0원", desc: "사용자 수만큼 내는 월 구독" },
  { title: "데이터 이전 지원", desc: "쓰던 시스템과 엑셀 데이터를 옮겨 드립니다" },
  { title: "상조 ERP 경험", desc: "상조사 전산을 직접 만들어 온 팀이 개발·운영합니다" },
  { title: "벤처기업 확인 · 소프트웨어 저작권 등록", desc: "" },
];

const termsStructure = [
  { icon: FiCheck, label: "시스템 이용권", value: "계약기간 동안 이용" },
  { icon: FiFileText, label: "소스코드", value: "고객 앱은 고객사" },
  { icon: FiRefreshCw, label: "데이터", value: "고객사 소유" },
];

const termsCases = [
  {
    type: "맞춤 시스템 구독",
    industry: "월 200만원부터",
    summary: "초기 구축비 없이 시작해 60개월 동안 구축·운영·개선을 한 팀이 맡는 계약",
    terms: [
      { label: "월 구독료", value: "월 200만원부터", note: "부가세 별도" },
      { label: "계약기간", value: "60개월", note: "이후 그때의 규모로 재산정해 갱신" },
      { label: "초기 구축비", value: "별도 없음", note: "설계·개발 비용은 구독료에 포함" },
      { label: "서버·인프라", value: "구독료에 포함", note: "운영·보안·백업 포함" },
      { label: "성과 연동 요금", value: "회사 유형별 산정", note: "계약 구조 예시 참고" },
      { label: "고객사 부담", value: "알림톡·AI 토큰 등", note: "사용량 기반 부가서비스" },
      { label: "소스코드", value: "고객 앱은 고객사", note: "함께 쓰는 관리 시스템은 솔컴바인" },
      { label: "데이터", value: "고객사 소유", note: "종료 시 합의된 형식으로 이전" },
    ],
    sample: "구축비 없이 시작하고, 60개월 동안 같은 팀이 만들고 운영하며 개선합니다",
  },
  {
    type: "정부지원금 구축",
    industry: "3,000만원부터",
    summary: "협약기간 안에 완성해 검수까지 마치는 일시 구축 계약",
    terms: [
      { label: "구축비", value: "3,000만원부터", note: "부가세 별도" },
      { label: "기본 범위", value: "화면 10개 기준", note: "화면 수·연동 범위에 따라 산정" },
      { label: "계약 형태", value: "일시 구축 계약", note: "협약기간 안에 검수 완료" },
      { label: "집행 비목", value: "외주용역비", note: "주관기관 기준 사전 확인" },
      { label: "증빙 서류", value: "견적서·계약서·검수확인서", note: "일체 제공" },
      { label: "전용 개발분", value: "고객사 귀속", note: "지원사업 규정에 따름" },
      { label: "성과 연동 요금", value: "없음", note: "일시 계약에는 두지 않음" },
      { label: "협약 종료 후", value: "전담 운영 구독 전환", note: "선택 사항" },
    ],
    sample: "협약기간 안에 완성해 검수까지 마치고, 이후 운영은 선택입니다",
  },
  {
    type: "전담 운영 구독",
    industry: "월 350만원부터",
    summary: "이미 운영 중인 시스템의 운영과 개선 개발을 함께 맡는 계약",
    terms: [
      { label: "월 운영료", value: "월 350만원부터", note: "부가세 별도" },
      { label: "대상", value: "운영 중인 웹·앱·ERP", note: "단일 업무 시스템 기준" },
      { label: "기본 범위", value: "운영 + 개선 개발", note: "합의된 범위 내" },
      { label: "월 비용에 포함", value: "유지보수와 추가 개발", note: "합의된 범위의 작업" },
      { label: "별도 사전 협의", value: "대규모 신규 구축", note: "운영 범위를 크게 벗어나는 개발" },
      { label: "협업 방식", value: "월간 계획·결과 보고", note: "시스템 규모별 주기 상이" },
      { label: "장애 대응", value: "시스템 중요도별", note: "운영 계약 예시 참고" },
      { label: "소스코드·데이터", value: "공통 원칙 적용", note: "위 공통 조건과 동일" },
    ],
    sample: "운영만 맡기는 것이 아니라 합의된 범위의 개선 개발까지 월 비용에 포함됩니다",
  },
];

const pageCopy = {
  subscription: {
    path: "subscription", section: "subscription",
    eyebrow: "CONTRACT EXAMPLES",
    title: "구독형 맞춤 시스템 개발",
    subtitle: "같은 구독이라도, 계약 구조는 회사마다 다릅니다.",
    intro: <>기본 구독료와 성과 연동 요금으로 구성됩니다. <b>성과 축이 없는 구독 계약은 만들지 않습니다.</b> 시스템이 회사를 키우면 저희 수익도 함께 커지는 구조여야 5년을 같은 편에서 갈 수 있기 때문입니다. 정부지원금 구축처럼 협약기간 안에 끝나는 일시 계약에는 성과 축을 두지 않습니다.</>,
    selectorLabel: "회사 유형 선택",
    sampleLabel: "계산 예시",
    note: "위 금액과 비율은 계약 구조를 설명하기 위한 예시입니다. 실제 조건은 사전 진단 후 개별 계약서에 정합니다.",
    consult: "계약 구조 상담하기",
    back: "구독 서비스로 돌아가기",
    structure, cases: contractCases,
  },
  operation: {
    path: "operation", section: "maintenance",
    eyebrow: "OPERATION CONTRACT EXAMPLES",
    title: "기존 시스템 전담 운영·유지보수",
    subtitle: "같은 운영이라도, 책임 범위는 시스템마다 다릅니다.",
    intro: <>시스템 수와 중요도, 장애 대응 수준, 매월 필요한 개선 작업량을 기준으로 운영 범위를 정합니다. <b>단순 문의 대응이 아니라 안정적인 운영과 개선 개발을 함께 책임집니다.</b></>,
    selectorLabel: "운영 유형 선택",
    sampleLabel: "운영 범위 예시",
    note: "위 금액과 대응 시간, 개선 작업량은 운영 구조를 설명하기 위한 예시입니다. 실제 조건은 사전 진단 후 개별 계약서에 정합니다.",
    consult: "운영 범위 상담하기",
    back: "운영 서비스로 돌아가기",
    structure: operationStructure, cases: operationCases,
  },
  terms: {
    path: "terms", section: "pricing",
    eyebrow: "CONTRACT TERMS",
    title: "상품별 계약 조건을 한자리에 모았습니다.",
    intro: <>구독료는 소프트웨어의 분할 구매대금이 아니라, 계약기간 동안 시스템을 구축하고 이용하며 운영·개선하기 위한 서비스 이용료입니다. <b>아래는 홈페이지에 안내된 조건을 상품별로 모은 것입니다.</b> 실제 계약서는 사전 진단 후 회사 상황에 맞춰 작성합니다.</>,
    selectorLabel: "상품 선택",
    selectedLabel: "선택한 상품",
    sampleLabel: "요약",
    note: "중도해지 위약 기준, 서비스 이전 금액 산식, 장애 대응 SLA처럼 개별 계약서에서 정하는 항목은 여기에 담지 않았습니다. 사전 진단 후 계약서에 명확히 정합니다.",
    consult: "계약 조건 상담하기",
    back: "서비스 안내로 돌아가기",
    structure: termsStructure, cases: termsCases,
  },
  serp: {
    path: "serp", section: "serp",
    eyebrow: "SERO ERP",
    title: "세로(SERO) 상조 ERP",
    subtitle: "행사 접수부터 정산·재고까지, 상조 업무를 한 화면에서",
    intro: <>상담에서 시작해 의전, 발주, 정산까지 하나의 흐름으로 이어집니다. <b>업무마다 따로 쓰던 엑셀과 수기 대장을 한 시스템으로 모읍니다.</b> 현재 출시를 준비하고 있으며, 실제 화면은 데모로 미리 보실 수 있습니다.</>,
    selectorLabel: "기능 선택",
    selectedLabel: "선택한 기능",
    sampleLabel: "업무 흐름 예시",
    note: "화면 구성과 세부 기능은 출시 준비 과정에서 달라질 수 있습니다. 확정된 화면은 데모에서 확인하실 수 있습니다.",
    consult: "출시 소식 문의하기",
    back: "세로 ERP로 돌아가기",
    demo: "상조 ERP 데모 보기",
    structure: serpStructure, cases: serpModules,
  },
  grant: {
    path: "grant", section: "grant",
    eyebrow: "GRANT BUILD EXAMPLES",
    title: "정부지원금으로 하는 시스템 구축",
    subtitle: "같은 지원금이라도, 집행 방식은 사업마다 다릅니다.",
    intro: <>지원사업마다 협약기간과 집행 가능한 비목이 다릅니다. <b>공고와 주관기관 세부관리기준을 먼저 확인해 계약 형태를 맞춥니다.</b> 그다음 협약기간을 역산해 검수까지 끝나는 일정으로 개발합니다.</>,
    selectorLabel: "지원사업 선택",
    selectedLabel: "선택한 지원사업",
    sampleLabel: "집행 예시",
    note: "협약기간과 집행 가능한 비목은 공고와 주관기관 세부관리기준에 따라 다릅니다. 계약 전에 함께 확인해 실제 조건을 정합니다.",
    consult: "지원금 도입 상담하기",
    back: "지원금 구축으로 돌아가기",
    structure: grantStructure, cases: grantCases,
  },
};

export default function SubscriptionCases({ mode = "subscription", language = "ko" }) {
  const navigate = useNavigate();
  const [selectedCase, setSelectedCase] = useState(0);
  const [colorTheme, setColorTheme] = useState(() => localStorage.getItem("sc-color-preview") || "default");
  const [fontScale, setFontScale] = useState(() => localStorage.getItem("sc-font-scale") || "normal");
  const copy = pageCopy[mode] || pageCopy.subscription;
  // /serp/features에서는 기본 서비스를 "세로 ERP 출시 문의"로 설정
  const defaultService = mode === "serp" ? "세로 ERP 출시 문의" : "맞춤 시스템 구독";
  const { consultOpen, consult, isSending, openConsult, closeConsult, updateConsult, submitConsult } = useConsultPopup({ defaultService });
  const pageStructure = copy.structure;
  const pageCases = copy.cases;
  const activeCase = pageCases[selectedCase];
  useLocaleDocument(language, mode === "grant" ? "grant-cases" : mode === "subscription" ? "cases" : mode);

  const changeColorPreview = (nextTheme) => {
    localStorage.setItem("sc-color-preview", nextTheme);
    setColorTheme(nextTheme);
  };
  const changeFontScale = (nextScale) => {
    localStorage.setItem("sc-font-scale", nextScale);
    setFontScale(nextScale);
  };
  const changeLanguage = (nextLanguage) => {
    localStorage.setItem("sc-language", nextLanguage);
    // 한국어는 /로 시작, 다른 언어는 /{언어}/로 시작
    const prefix = nextLanguage === "ko" ? "" : `/${nextLanguage}`;
    slideTo(navigate, `${prefix}/${copy.path}/cases`, undefined, nextLanguage === "en" ? "forward" : "back");
  };

  // 한국어는 /로, 다른 언어는 /{언어}로 이동
  const homePath = language === "ko" ? "/" : `/${language}`;
  const goHome = (state) => slideTo(navigate, homePath, { state }, "back");
  const goBack = () => goHome({ scrollTo: copy.section });

  // 전환 스냅샷이 찍히기 전에 최상단으로 올려야 한다.
  useLayoutEffect(() => {
    scrollToTop();
  }, []);

  // ESC 키: 팝업이 열려 있으면 무시(팝업이 처리), 닫혀 있으면 뒤로 가기
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !consultOpen) goBack();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  return (
    <LocalizedContent lang={language}><div className={`case-page font-${fontScale} ${colorTheme !== "default" ? `palette-theme ${colorTheme}-theme` : ""}`}>
      <header className="case-topbar">
        <button type="button" className="case-back" onClick={goBack}><FiArrowLeft/> {copy.back}</button>
        <div className="case-topbar-actions"><label className="language-select"><FiGlobe/><select value={language} onChange={(event) => changeLanguage(event.target.value)} aria-label="언어 선택">{locales.map(({id,label})=><option value={id} key={id} data-no-translate="true">{label}</option>)}</select></label><div className="theme-switcher" role="group" aria-label="색상 테마 선택"><span>컬러</span>{colorThemes.map(({ id, label, color }) => <button type="button" key={id} className={colorTheme === id ? "is-active" : ""} onClick={() => changeColorPreview(id)} aria-label={`${label} 테마`} aria-pressed={colorTheme === id} title={label}><i style={{ background: color }}/></button>)}</div><div className="font-switcher" role="group" aria-label="글씨 크기 조절"><span>글씨</span>{[{ id: "small", label: "작게", text: "가−" }, { id: "normal", label: "기본", text: "가" }, { id: "large", label: "크게", text: "가+" }].map(({ id, label, text }) => <button type="button" key={id} className={fontScale === id ? "is-active" : ""} onClick={() => changeFontScale(id)} aria-label={`글씨 ${label}`} aria-pressed={fontScale === id} title={`글씨 ${label}`}>{text}</button>)}</div><span className="case-topbar-brand">SOL<span>COMBINE</span></span></div>
      </header>

      <main className="case-body">
        <div className="case-intro">
          <div>
            <span className="eyebrow">{copy.eyebrow}</span>
            <h1>{copy.title}</h1>
            {copy.subtitle && <p className="case-subtitle">{copy.subtitle}</p>}
            <p>{copy.intro}</p>
          </div>
          <div className="structure-strip" aria-label="모든 계약의 공통 원칙">
            {pageStructure.map(({ icon: Icon, label, value }) => (
              <article key={label}>
                <Icon/><span><small>{label}</small><strong>{value}</strong></span>
              </article>
            ))}
          </div>
        </div>

        <div className={`case-selector${pageCases.length === 5 ? " is-five" : pageCases.length === 3 ? " is-three" : ""}`} role="tablist" aria-label={copy.selectorLabel}>
          {pageCases.map(({ type, industry, terms }, index) => (
            <button
              type="button"
              role="tab"
              aria-selected={selectedCase === index}
              className={selectedCase === index ? "is-active" : ""}
              onClick={() => setSelectedCase(index)}
              key={type}
            >
              {selectedCase === index && <FiCheck className="case-selector-check" aria-hidden="true"/>}
              <small>{industry}</small><strong>{type}</strong><span>{terms[1].value}</span>
            </button>
          ))}
        </div>

        <article className="contract-detail" role="tabpanel">
          <div className="contract-detail-head">
            <div><span className="contract-industry">{activeCase.industry}</span><h2>{activeCase.type}</h2><p>{activeCase.summary}</p></div>
            <span className="selected-label"><FiCheck/> {copy.selectedLabel || "선택한 계약 예시"}</span>
          </div>
          <dl className="contract-terms">
            {activeCase.terms.map(({ label, value, note }, index) => (
              <div className={index < 2 ? "is-key" : ""} key={label}>
                <dt>{label}</dt>
                <dd><strong>{value}</strong><small>{note}</small></dd>
              </div>
            ))}
          </dl>
          <p className="contract-sample"><em>{copy.sampleLabel}</em><strong>{activeCase.sample}</strong></p>
        </article>

        {mode === "serp" && (
          <>
            {/* 이런 고민 있으신가요? - 한국어만 */}
            {language === "ko" && (
              <section className="serp-pain-points" aria-label="이런 고민 있으신가요?">
                <h2>이런 고민 있으신가요?</h2>
                <ul>
                  {serpPainPoints.map((point) => <li key={point}>{point}</li>)}
                </ul>
              </section>
            )}

            <section className="module-overview" aria-label="세로 ERP 전체 기능">
              <h2>{language === "ko" ? "여덟 개 기능, 한눈에 보기" : "All modules at a glance"}</h2>
              <div className="module-overview-grid">
                {pageCases.map(({ type, industry, summary, terms, sample }) => (
                  <article key={type}>
                    <small>{industry}</small>
                    <h3>{type}</h3>
                    <p>{summary}</p>
                    <ul>
                      {terms.map(({ label, value, note }) => <li key={label}><strong>{label}</strong><span>{value}</span><em>{note}</em></li>)}
                    </ul>
                    <p className="module-overview-flow">{sample}</p>
                  </article>
                ))}
              </div>
            </section>

            {/* 업무 흐름 6단계 - 한국어만 */}
            {language === "ko" && (
              <section className="serp-workflow" aria-label="업무 흐름">
                <h2>업무 흐름</h2>
                <ol className="serp-workflow-steps">
                  {serpWorkflow.map(({ step, title, who }) => (
                    <li key={step}>
                      <span className="step-number">{step}</span>
                      <strong>{title}</strong>
                      <small>{who}</small>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {/* 선불식 상조사 안내 - 한국어만 */}
            {language === "ko" && (
              <section className="serp-prepaid" aria-label="선불식 상조사">
                <h2>선불식 상조사라면</h2>
                <p>회원·납입·선수금 관리 기능도 함께 준비하고 있습니다. 함께 만들어 갈 파일럿 고객사를 모시고 있습니다.</p>
              </section>
            )}

            {/* 도입 부담은 낮추고 - 한국어만 */}
            {language === "ko" && (
              <section className="serp-benefits" aria-label="도입 부담은 낮추고">
                <h2>도입 부담은 낮추고</h2>
                <ul className="serp-benefits-list">
                  {serpBenefits.map(({ title, desc }) => (
                    <li key={title}><strong>{title}</strong>{desc && <span>{desc}</span>}</li>
                  ))}
                </ul>
              </section>
            )}

            {/* 맞춤 개발 안내 - 한국어만 */}
            {language === "ko" && (
              <section className="serp-custom" aria-label="우리 회사 방식에 맞춰">
                <h2>우리 회사 방식에 맞춰 만들어 드립니다</h2>
                <p>SERO 프레임 위에 회사에 필요한 기능을 더해 개발합니다. 초기 개발비 대신 구독형으로 시작할 수 있고, 유지보수와 추가 개발도 솔컴바인이 맡습니다.</p>
              </section>
            )}
          </>
        )}

        <div className="case-foot">
          <p className="case-note"><FiHelpCircle/> {copy.note}</p>
          <div className="case-foot-actions">
            <button type="button" className="case-consult" onClick={openConsult}>{copy.consult} <FiArrowRight/></button>
            {copy.demo && <a className="case-demo" href="https://erp.seroerp.com/login?utm_source=homepage" target="_blank" rel="noopener noreferrer">{copy.demo} <FiExternalLink/></a>}
            <button type="button" className="case-back-inline" onClick={goBack}><FiArrowLeft/> 돌아가기</button>
          </div>
        </div>
      </main>
      <ConsultPopup
        consultOpen={consultOpen}
        consult={consult}
        isSending={isSending}
        closeConsult={closeConsult}
        updateConsult={updateConsult}
        submitConsult={submitConsult}
      />
    </div></LocalizedContent>
  );
}
